// CMS User Detail Page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, Ticket, CreditCard,
  Ban, CheckCircle, Download, Edit, Save, X, RefreshCw, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, hasRole } = useCMS();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cms/users/${userId}`, { headers: getAuthHeaders() });
      setData(res.data);
      setEditForm({
        first_name: res.data.user.first_name || '',
        last_name: res.data.user.last_name || '',
        phone: res.data.user.phone || '',
        country: res.data.user.country || '',
        city: res.data.user.city || '',
        address: res.data.user.address || '',
        postal_code: res.data.user.postal_code || ''
      });
    } catch (err) {
      toast.error('Failed to load user');
      navigate('/cms/users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const handleSave = async () => {
    try {
      await axios.patch(`${API}/cms/users/${userId}`, editForm, { headers: getAuthHeaders() });
      toast.success('User updated');
      setEditing(false);
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleSuspend = async (suspend) => {
    const reason = suspend ? prompt('Enter suspension reason:') : null;
    if (suspend && !reason) return;

    try {
      const endpoint = suspend 
        ? `${API}/cms/users/${userId}/suspend?reason=${encodeURIComponent(reason)}`
        : `${API}/cms/users/${userId}/reactivate`;
      
      await axios.post(endpoint, {}, { headers: getAuthHeaders() });
      toast.success(suspend ? 'User suspended' : 'User reactivated');
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/cms/users/${userId}/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_${userId}_export.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { user, bookings, payments } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/cms/users')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{user.first_name} {user.last_name}</h1>
          <p className="text-slate-400">{user.email}</p>
        </div>
        <div className="flex gap-2">
          {hasRole('super_admin') && (
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {hasRole('super_admin', 'support') && (
            <Button 
              variant={user.is_suspended ? 'default' : 'destructive'}
              onClick={() => handleSuspend(!user.is_suspended)}
            >
              {user.is_suspended ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Reactivate
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {user.is_suspended && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-lg">
          <p className="font-medium">User Suspended</p>
          <p className="text-sm">{user.suspension_reason}</p>
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Profile</CardTitle>
            {hasRole('super_admin', 'support') && !editing && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                <Edit className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={editForm.country}
                    onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">{user.email}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">{user.phone || 'Not provided'}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">
                    {user.city || ''} {user.country || 'Not provided'}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">Registered: {formatDate(user.created_at)}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                  <Ticket className="w-4 h-4 text-slate-400" />
                  <div>
                    {user.pass_type ? (
                      <Badge variant={user.pass_type === 'annual' ? 'default' : 'secondary'}>
                        {user.pass_type} pass
                      </Badge>
                    ) : (
                      <span className="text-sm text-slate-400">No pass</span>
                    )}
                    {user.pass_expiry && (
                      <p className="text-xs text-slate-400 mt-1">
                        Expires: {formatDate(user.pass_expiry)}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Tabs for Bookings & Payments */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
          <Tabs defaultValue="bookings" className="w-full">
            <CardHeader>
              <TabsList className="bg-slate-900">
                <TabsTrigger value="bookings">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Bookings ({bookings?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="payments">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payments ({payments?.length || 0})
                </TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent>
              <TabsContent value="bookings" className="mt-0">
                {bookings?.length > 0 ? (
                  <div className="space-y-3">
                    {bookings.map((b, i) => (
                      <div key={i} className="p-4 bg-slate-900/50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{b.hotel_name || b.hotel_id}</p>
                            <p className="text-sm text-slate-400">{b.destination}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatDate(b.check_in)} - {formatDate(b.check_out)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">€{b.total_price?.toFixed(2)}</p>
                            <Badge variant={b.status === 'confirmed' ? 'default' : 'secondary'}>
                              {b.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No bookings found</p>
                )}
              </TabsContent>
              <TabsContent value="payments" className="mt-0">
                {payments?.length > 0 ? (
                  <div className="space-y-3">
                    {payments.map((p, i) => (
                      <div key={i} className="p-4 bg-slate-900/50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{p.description || p.pass_type || 'Payment'}</p>
                            <p className="text-xs text-slate-500">{formatDate(p.created_at)}</p>
                            {p.stripe_payment_intent && (
                              <p className="text-xs text-slate-600 font-mono mt-1">
                                {p.stripe_payment_intent}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold">€{p.amount?.toFixed(2)}</p>
                            <Badge variant={
                              p.status === 'paid' ? 'default' : 
                              p.status === 'refunded' ? 'destructive' : 'secondary'
                            }>
                              {p.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No payments found</p>
                )}
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default CMSUserDetail;
